
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Upload, Instagram, Youtube, Linkedin, FileVideo2, Twitter, Facebook, Video, Globe, Edit, Theater, Mic, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";

const expressionForms = [
  { id: "edit", label: "Edit", icon: Edit },
  { id: "react", label: "React", icon: FileVideo2 },
  { id: "acting", label: "Acting", icon: Theater },
  { id: "other", label: "Other", icon: Mic },
];

const socialPlatforms = [
  { id: "tiktok", name: "TikTok", icon: Video },
  { id: "instagram", name: "Instagram", icon: Instagram },
  { id: "youtube", name: "YouTube", icon: Youtube },
  { id: "linkedin", name: "LinkedIn", icon: Linkedin },
  { id: "twitter", name: "Twitter", icon: Twitter },
  { id: "facebook", name: "Facebook", icon: Facebook },
  { id: "website", name: "Website", icon: Globe }
];

interface ProfileProps {
  userProfile: {
    id: string;
    username?: string;
    full_name?: string;
    bio?: string;
    avatar_url?: string;
    expression_types?: string[];
    social_links?: Record<string, string>;
  } | null;
  formState: {
    name: string;
    username: string;
    bio: string;
    expressions: string[];
    socialProfiles: Array<{
      platform: string;
      url: string;
      username: string;
    }>;
  };
  onFormStateChange: (newState: Partial<{
    name: string;
    username: string;
    bio: string;
    expressions: string[];
    socialProfiles: Array<{
      platform: string;
      url: string;
      username: string;
    }>;
  }>) => void;
}

const ProfileManagement = ({ userProfile, formState, onFormStateChange }: ProfileProps) => {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(userProfile?.avatar_url);
  
  // Use controlled components with the formState from parent
  const { name, username, bio, expressions, socialProfiles } = formState;
  
  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0]) return;
    
    const file = event.target.files[0];
    const fileSize = file.size / 1024 / 1024; // Size in MB
    
    // Check file size (limit to 2MB)
    if (fileSize > 2) {
      toast.error("Image too large. Maximum size is 2MB.");
      return;
    }
    
    const fileExt = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp'];
    
    // Validate file type
    if (!fileExt || !allowedExtensions.includes(fileExt)) {
      toast.error("Invalid file type. Please upload JPG, PNG or WebP images.");
      return;
    }
    
    // Create a path that includes the user's ID to comply with RLS policies
    // Format: userId/randomString.extension
    const filePath = `${user?.id}/${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
    
    try {
      setIsSaving(true);
      toast.info("Uploading avatar...");
      
      const { error: uploadError, data } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });
      
      if (uploadError) {
        toast.error("Error uploading avatar: " + uploadError.message);
        return;
      }
      
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);
      
      if (urlData) {
        const { error } = await supabase
          .from('profiles')
          .update({ avatar_url: urlData.publicUrl })
          .eq('id', user?.id);
        
        if (error) {
          toast.error("Error updating avatar URL: " + error.message);
          return;
        }
        
        // Update local state instead of reloading the page
        setAvatarUrl(urlData.publicUrl);
        toast.success("Avatar updated successfully");
      }
    } catch (error) {
      toast.error("Error uploading avatar: " + (error as Error).message);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDeleteAvatar = async () => {
    if (!user || !avatarUrl) return;
    
    try {
      setIsSaving(true);
      toast.info("Removing avatar...");
      
      // Only update the profile record to remove the avatar_url reference
      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', user.id);
      
      if (error) {
        toast.error("Error removing avatar: " + error.message);
        return;
      }
      
      // Update local state
      setAvatarUrl(undefined);
      toast.success("Avatar removed successfully");
      
    } catch (error) {
      toast.error("Error removing avatar: " + (error as Error).message);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleSaveProfile = async () => {
    if (!user) {
      toast.error("You must be logged in to update your profile");
      return;
    }
    
    try {
      setIsSaving(true);
      
      const socialLinks: Record<string, string> = {};
      socialProfiles.forEach(profile => {
        if (profile.url) {
          socialLinks[profile.platform] = profile.url;
        }
      });

      const { error, data } = await supabase
        .from('profiles')
        .update({
          full_name: name,
          username: username,
          bio: bio,
          expression_types: expressions,
          social_links: socialLinks
        })
        .eq('id', user.id)
        .select();
      
      if (error) {
        if (error.message.includes('policy')) {
          toast.error("Permission denied: You can only update your own profile");
        } else {
          toast.error("Failed to save profile: " + error.message);
        }
        return;
      }
      
      toast.success("Profile saved successfully");
    } catch (error) {
      toast.error("Failed to save profile: " + (error as Error).message);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Use the onFormStateChange handler to update parent state
  const updateSocialProfile = (platformId: string, field: string, value: string) => {
    const updatedSocialProfiles = socialProfiles.map(profile => 
      profile.platform === platformId 
        ? { ...profile, [field]: value } 
        : profile
    );
    
    onFormStateChange({ socialProfiles: updatedSocialProfiles });
  };
  
  const getSocialIcon = (platformId: string) => {
    const platform = socialPlatforms.find(p => p.id === platformId);
    if (platform) {
      const Icon = platform.icon;
      return <Icon className="h-4 w-4" />;
    }
    return <FileVideo2 className="h-4 w-4" />;
  };

  const toggleExpression = (expressionId: string) => {
    const updatedExpressions = expressions.includes(expressionId)
      ? expressions.filter(id => id !== expressionId)
      : [...expressions, expressionId];
    
    onFormStateChange({ expressions: updatedExpressions });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
          <CardDescription>Update your basic profile information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="flex flex-col items-center space-y-2">
              <Avatar className="h-24 w-24">
                <AvatarImage src={avatarUrl || "/placeholder.svg"} alt={name} />
                <AvatarFallback>{name.substring(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              
              <div className="text-xs text-muted-foreground text-center mt-1 mb-2">
                Recommended: 400x400 pixels
              </div>
              
              <div className="flex flex-col space-y-2 w-full">
                <Button variant="outline" size="sm" asChild>
                  <label htmlFor="avatar-upload" className="cursor-pointer flex items-center justify-center">
                    <Upload className="mr-2 h-4 w-4" />
                    Upload
                    <input 
                      id="avatar-upload" 
                      type="file" 
                      className="hidden" 
                      accept="image/png, image/jpeg, image/webp" 
                      onChange={handleAvatarUpload} 
                      disabled={isSaving}
                    />
                  </label>
                </Button>
                
                {avatarUrl && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleDeleteAvatar} 
                    disabled={isSaving}
                    className="flex items-center justify-center text-destructive hover:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Remove
                  </Button>
                )}
              </div>
            </div>
            
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input 
                    id="name" 
                    value={name} 
                    onChange={(e) => onFormStateChange({ name: e.target.value })} 
                    placeholder="Your full name" 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input 
                    id="username" 
                    value={username} 
                    onChange={(e) => onFormStateChange({ username: e.target.value })} 
                    placeholder="Your username" 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Textarea 
                  id="bio" 
                  value={bio} 
                  onChange={(e) => onFormStateChange({ bio: e.target.value })} 
                  placeholder="Tell us about yourself" 
                  rows={4} 
                />
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Forms of Expression</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2">
              {expressionForms.map((form) => (
                <div
                  key={form.id}
                  className={`flex items-center space-x-2 border rounded-md p-3 cursor-pointer hover:bg-accent/50 transition-colors ${
                    expressions.includes(form.id) ? "border-primary bg-primary/10" : "border-border"
                  }`}
                  onClick={() => toggleExpression(form.id)}
                >
                  <Checkbox
                    id={`expression-${form.id}`}
                    checked={expressions.includes(form.id)}
                    onCheckedChange={() => toggleExpression(form.id)}
                    className="data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                  />
                  <div className="flex items-center space-x-2">
                    <form.icon className="h-4 w-4" />
                    <Label
                      htmlFor={`expression-${form.id}`}
                      className="cursor-pointer font-medium"
                    >
                      {form.label}
                    </Label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button onClick={handleSaveProfile} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </CardFooter>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Links</CardTitle>
          <CardDescription>Connect your social media accounts - only filled profiles will be displayed publicly</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {socialProfiles.map((profile) => (
            <div key={profile.platform} className="flex items-center space-x-4">
              <div className="w-10 flex justify-center">
                {getSocialIcon(profile.platform)}
              </div>
              <div className="flex-1">
                <Label htmlFor={`username-${profile.platform}`} className="sr-only">
                  {profile.platform} Username
                </Label>
                <Input 
                  id={`username-${profile.platform}`}
                  value={profile.username} 
                  onChange={(e) => updateSocialProfile(profile.platform, 'username', e.target.value)} 
                  placeholder={`${socialPlatforms.find(p => p.id === profile.platform)?.name} Username`} 
                />
              </div>
              <div className="flex-1">
                <Label htmlFor={`url-${profile.platform}`} className="sr-only">
                  {profile.platform} URL
                </Label>
                <Input 
                  id={`url-${profile.platform}`}
                  value={profile.url} 
                  onChange={(e) => updateSocialProfile(profile.platform, 'url', e.target.value)} 
                  placeholder={`${socialPlatforms.find(p => p.id === profile.platform)?.name} URL`} 
                />
              </div>
              {profile.url && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="p-0 h-8 w-8 rounded-full" 
                  asChild
                >
                  <a href={profile.url} target="_blank" rel="noopener noreferrer">
                    <Globe className="h-4 w-4" />
                  </a>
                </Button>
              )}
            </div>
          ))}
        </CardContent>
        <CardFooter>
          <Button onClick={handleSaveProfile} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Social Profiles"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default ProfileManagement;
